import React, { useCallback, useRef, useState } from "react";

import {
	faAngleDoubleDown,
	faAngleDoubleUp,
	faDollarSign,
	faHashtag,
	faPercentage,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Popover from "@radix-ui/react-popover";
import cx from "classnames";

import { Button } from "~/ui";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import { TextField } from "~/ui/legacyFellowIndex";
import Tooltip from "~/ui/Tooltip";

import type { MetricProgress, MetricType } from "~/__generated__/ObjectiveQuery_Info.graphql";

import styles from "./TargetInput.module.css";

enum ErrorFields {
	START,
	CURRENT,
	TARGET,
}

enum ErrorMessages {
	VALUE_IS_NAN = "Value must be a number.",
	START_MUST_BE_LARGER_THAN_TARGET = `Start must be larger than target. You can also change progress type
	through the dropdown above.`,
	START_MUST_BE_SMALLER_THAN_TARGET = `Start must be smaller than target. You can also change progress type
	through the dropdown above.`,
}

interface ErrorState {
	field: ErrorFields;
	message: ErrorMessages;
}

interface TargetInputProps {
	progress: MetricProgress;
	type: MetricType;
	currency?: string;
	start: number;
	current: number;
	target: number;
	onSubmit(
		progress: MetricProgress,
		type: MetricType,
		currency: string | undefined,
		start: number,
		current: number,
		target: number,
	): void;
	className?: string;
	popupClassName?: string;
	hasAccess?: boolean;
	isCreateView?: boolean;
}

export enum MetricProgresses {
	INCREASING = "INCREASING",
	DECREASING = "DECREASING",
}

const MetricProgressesNames: { [key: string]: string } = {
	[MetricProgresses.INCREASING]: "Increasing",
	[MetricProgresses.DECREASING]: "Decreasing",
};

const MetricProgressesIcons: { [key: string]: IconDefinition } = {
	[MetricProgresses.INCREASING]: faAngleDoubleUp,
	[MetricProgresses.DECREASING]: faAngleDoubleDown,
};

export enum MetricTypes {
	NUMBER = "NUMBER",
	PERCENTAGE = "PERCENTAGE",
	CURRENCY = "CURRENCY",
}

const MetricTypesNames: { [key: string]: string } = {
	[MetricTypes.NUMBER]: "Number",
	[MetricTypes.PERCENTAGE]: "Percentage",
	[MetricTypes.CURRENCY]: "Currency",
};

const MetricTypesIcons: { [key: string]: IconDefinition } = {
	[MetricTypes.NUMBER]: faHashtag,
	[MetricTypes.PERCENTAGE]: faPercentage,
	[MetricTypes.CURRENCY]: faDollarSign,
};

const initialDecreasingValues = {
	start: 100,
	current: 100,
	target: 50,
};

export default function TargetInput({
	progress: initialProgress,
	type: initialType,
	currency: initialCurrency,
	start: initialStart,
	current: initialCurrent,
	target: initialTarget,
	onSubmit,
	className,
	hasAccess,
	isCreateView,
}: TargetInputProps) {
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [progress, setProgress] = useState<MetricProgress>(initialProgress);
	const [type, setType] = useState<MetricType>(initialType);
	const [currency, setCurrency] = useState<string>(initialCurrency || "$");
	const [start, setStart] = useState<string>(initialStart.toString());
	const [current, setCurrent] = useState<string>(initialCurrent.toString());
	const [target, setTarget] = useState<string>(initialTarget.toString());
	const [error, setError] = useState<ErrorState>();
	const progressRef = useRef<HTMLDivElement>(null);
	const typeRef = useRef<HTMLDivElement>(null);

	const onChangeProgress = useCallback(
		(progress: MetricProgress) => {
			setProgress(progress);

			// If create view, reset values to initial values when changing progress direction
			if (isCreateView) {
				if (progress === MetricProgresses.DECREASING) {
					setStart(initialDecreasingValues.start.toString());
					setCurrent(initialDecreasingValues.current.toString());
					setTarget(initialDecreasingValues.target.toString());
				} else {
					setStart(initialStart.toString());
					setCurrent(initialCurrent.toString());
					setTarget(initialTarget.toString());
				}
				setError(undefined);
				return;
			}

			// Clear progress direction error if exists
			if (
				error?.message === ErrorMessages.START_MUST_BE_LARGER_THAN_TARGET ||
				error?.message === ErrorMessages.START_MUST_BE_SMALLER_THAN_TARGET
			) {
				setError(undefined);
				return;
			}

			// If there was no errors and user changed progress direction, that means there's an error now
			if (start !== target) {
				if (progress == MetricProgresses.DECREASING) {
					setError({ field: ErrorFields.START, message: ErrorMessages.START_MUST_BE_LARGER_THAN_TARGET });
				} else {
					setError({ field: ErrorFields.START, message: ErrorMessages.START_MUST_BE_SMALLER_THAN_TARGET });
				}
			}
		},
		[error?.message, initialCurrent, initialStart, initialTarget, isCreateView, start, target],
	);

	const onChangeType = useCallback(
		(type: MetricType) => {
			setType(type);
		},
		[setType],
	);

	const onChangeCurrency = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (event.target.value.length > 3) {
				return;
			}
			setCurrency(event.target.value);
		},
		[setCurrency],
	);

	const onChangeStart = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setError(undefined);
			setStart(event.target.value);
		},
		[setStart],
	);

	const onChangeCurrent = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			if (error?.field === ErrorFields.CURRENT) {
				setError(undefined);
			}
			setCurrent(event.target.value);
		},
		[setCurrent, error?.field],
	);

	const onChangeTarget = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setError(undefined);
			setTarget(event.target.value);
		},
		[setTarget],
	);

	const validateProgressValues = useCallback(() => {
		const parsedStart = parseFloat(start);
		const parsedTarget = parseFloat(target);

		if (progress === MetricProgresses.INCREASING && parsedStart > parsedTarget) {
			setError({ field: ErrorFields.START, message: ErrorMessages.START_MUST_BE_SMALLER_THAN_TARGET });
			return false;
		} else if (progress === MetricProgresses.DECREASING && parsedStart < parsedTarget) {
			setError({ field: ErrorFields.START, message: ErrorMessages.START_MUST_BE_LARGER_THAN_TARGET });
			return false;
		}

		setError(undefined);
		return true;
	}, [progress, start, target]);

	const onConfirm = useCallback(() => {
		const parsedStart = parseFloat(start);
		if (isNaN(parsedStart)) {
			setError({ field: ErrorFields.START, message: ErrorMessages.VALUE_IS_NAN });
			return;
		}
		const parsedCurrent = parseFloat(current);
		if (isNaN(parsedCurrent)) {
			setError({ field: ErrorFields.CURRENT, message: ErrorMessages.VALUE_IS_NAN });
			return;
		}
		const parsedTarget = parseFloat(target);
		if (isNaN(parsedTarget)) {
			setError({ field: ErrorFields.TARGET, message: ErrorMessages.VALUE_IS_NAN });
			return;
		}
		if (!validateProgressValues()) {
			return;
		}
		onSubmit(progress, type, currency, parsedStart, parsedCurrent, parsedTarget);
		setIsOpen(false);
	}, [start, current, target, validateProgressValues, onSubmit, progress, type, currency]);

	const onKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Enter") {
				onConfirm();
				return;
			}
			if (event.key === "Escape") {
				event.stopPropagation();
			}
		},
		[onConfirm],
	);

	const ProgressItem = ({ progress }: Pick<TargetInputProps, "progress">) => {
		const onClick = useCallback(() => {
			onChangeProgress(progress);
		}, [progress]);

		return (
			<DropdownItem
				icon={<FontAwesomeIcon icon={MetricProgressesIcons[progress]} className={styles.optionIcon} fixedWidth />}
				onClick={onClick}
			>
				{MetricProgressesNames[progress]}
			</DropdownItem>
		);
	};

	const TypeItem = ({ type }: Pick<TargetInputProps, "type">) => {
		const onClick = useCallback(() => {
			onChangeType(type);
		}, [type]);

		return (
			<DropdownItem
				icon={<FontAwesomeIcon icon={MetricTypesIcons[type]} className={styles.optionIcon} fixedWidth />}
				onClick={onClick}
			>
				{MetricTypesNames[type]}
			</DropdownItem>
		);
	};

	const abbreviateValue = useCallback((value: number) => {
		return Intl.NumberFormat("en", { notation: "compact", maximumSignificantDigits: 3 }).format(value);
	}, []);

	return (
		<Popover.Root open={isOpen} onOpenChange={setIsOpen}>
			<Popover.Trigger asChild>
				<Button size="wide" variant="tertiary" className={cx(styles.trigger, className)} disabled={!hasAccess}>
					{type === MetricTypes.CURRENCY && currency}
					<span>{abbreviateValue(initialStart)}</span>/<span>{abbreviateValue(initialCurrent)}</span>/
					<span>{abbreviateValue(initialTarget)}</span>
					{type === MetricTypes.PERCENTAGE && "%"}
				</Button>
			</Popover.Trigger>
			<Popover.Content side="bottom" align="end" sideOffset={10} className={styles.popup}>
				<div className={styles.header}>
					<h3>Key result</h3>
					<Tooltip content={"Progress"}>
						<ArrowDropdown
							heading="Progress"
							text={<FontAwesomeIcon icon={MetricProgressesIcons[progress]} fixedWidth />}
							disabled={!hasAccess}
							className={styles.dropdown}
							forceCloseOnOutsideClick
						>
							<div ref={progressRef} className={styles.menuList}>
								{Object.keys(MetricProgressesNames).map(progressName => (
									<ProgressItem key={progressName} progress={progressName as MetricProgress} />
								))}
							</div>
						</ArrowDropdown>
					</Tooltip>
					<Tooltip content={"Value type"}>
						<ArrowDropdown
							heading="Type"
							text={<FontAwesomeIcon icon={MetricTypesIcons[type]} fixedWidth />}
							disabled={!hasAccess}
							className={styles.dropdown}
							forceCloseOnOutsideClick
						>
							<div ref={typeRef} className={styles.menuList}>
								{Object.keys(MetricTypesNames).map(metricName => (
									<TypeItem key={metricName} type={metricName as MetricType} />
								))}
							</div>
						</ArrowDropdown>
					</Tooltip>
				</div>
				<div className={styles.content}>
					<div className={styles.values}>
						{type === MetricTypes.CURRENCY && (
							<div className={styles.currency}>
								<label>Currency</label>
								<TextField value={currency} onChange={onChangeCurrency} onKeyDown={onKeyDown} />
							</div>
						)}
						<div className={cx(styles.metrics, { [styles.withCurrency]: type === MetricTypes.CURRENCY })}>
							<div>
								<label>Start</label>
								<TextField
									value={start.toString()}
									onChange={onChangeStart}
									onKeyDown={onKeyDown}
									className={cx({ [styles.errorInput]: error?.field === ErrorFields.START })}
									onBlur={validateProgressValues}
								/>
							</div>
							<span>/</span>
							<div>
								<label>Current</label>
								<TextField
									value={current.toString()}
									onChange={onChangeCurrent}
									onKeyDown={onKeyDown}
									className={cx({ [styles.errorInput]: error?.field === ErrorFields.CURRENT })}
									autoFocus
								/>
							</div>
							<span>/</span>
							<div>
								<label>Target</label>
								<TextField
									value={target.toString()}
									onChange={onChangeTarget}
									onKeyDown={onKeyDown}
									className={cx({ [styles.errorInput]: error?.field === ErrorFields.TARGET })}
									onBlur={validateProgressValues}
								/>
							</div>
						</div>
					</div>
					{error && <p className={styles.errorText}>{error.message}</p>}
				</div>

				<Button variant="primary" size="normal" onClick={onConfirm} disabled={!!error}>
					Save
				</Button>
			</Popover.Content>
		</Popover.Root>
	);
}
